/* eslint-disable react-hooks/exhaustive-deps */
/**
 * FleetFuelTopup.js
 * Standalone fuel top-up log.
 * Accessible from: Trip Logs page (low tank warning), Vehicle detail, Fleet nav.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, InputNumber, DatePicker, Select, Modal,
         message, Tag, Spin, Tooltip } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import FleetService from '../../services/FleetService';
import moment from 'moment';

const { Option }   = Select;
const { confirm }  = Modal;

const toN  = v => Number(v || 0);
const fmtD = v => (!v || v === '0000-00-00') ? '—' : moment(v).format('DD MMM YYYY');

export default function FleetFuelTopup() {
    const [topups,     setTopups]    = useState([]);
    const [vehicles,   setVehicles]  = useState([]);
    const [loading,    setLoading]   = useState(false);
    const [modalOpen,  setModalOpen] = useState(false);
    const [saving,     setSaving]    = useState(false);
    const [form,       setForm]      = useState({});
    const [filterVeh,  setFilterVeh] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [tr, vr] = await Promise.all([
                FleetService.getFuelTopups(filterVeh),
                FleetService.getVehiclesList(),
            ]);
            if (Number(tr?.data?.code) === 200) setTopups(tr.data.result || []);
            if (Number(vr?.data?.code) === 200) setVehicles(vr.data.result || []);
        } catch { message.error('Failed to load'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, [filterVeh]);

    const openNew = (vehicle_id = '') => {
        setForm({ topup_date: moment().format('YYYY-MM-DD'), vehicle_id });
        setModalOpen(true);
    };

    const save = async () => {
        if (!form.vehicle_id || !form.litres_added) {
            message.error('Vehicle and litres are required');
            return;
        }
        setSaving(true);
        try {
            const r = await FleetService.saveFuelTopup(form);
            if (Number(r?.data?.code) === 200) {
                message.success('Top-up recorded');
                setModalOpen(false);
                load();
            } else {
                message.error(r?.data?.message || 'Failed to save');
            }
        } catch { message.error('Error saving'); }
        setSaving(false);
    };

    const del = (id) => {
        confirm({
            title: 'Delete this top-up record?',
            icon: <ExclamationCircleOutlined/>,
            okType: 'danger', okText: 'Delete',
            onOk: async () => {
                await FleetService.deleteFuelTopup(id);
                message.success('Deleted');
                load();
            },
        });
    };

    // Vehicles with estimated low balance
    const lowVehicles = vehicles.filter(v =>
        toN(v.initial_fuel_level) === 0 && !topups.find(t => t.vehicle_id === v.id)
    );

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <p className="page-title">Fuel Top-ups</p>
                    <p className="page-subtitle">Record fuel added to vehicles — separate from trip logging</p>
                </div>
                <div className="my-auto ml-auto">
                    <Button type="primary" icon={<PlusOutlined/>} onClick={() => openNew()}>
                        Record Top-up
                    </Button>
                </div>
            </div>

            <div className="page-pad">
                {/* Filter */}
                <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
                    <Select allowClear placeholder="All Vehicles" style={{ width:200 }}
                        value={filterVeh || undefined} onChange={v => setFilterVeh(v || '')}>
                        {vehicles.map(v => (
                            <Option key={v.id} value={String(v.id)}>{v.registration_no}</Option>
                        ))}
                    </Select>
                </div>

                <Spin spinning={loading}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-md table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase">
                                <tr>
                                    <th>Date</th>
                                    <th>Vehicle</th>
                                    <th style={{ textAlign:'right' }}>Litres Added</th>
                                    <th style={{ textAlign:'right' }}>Cost (E)</th>
                                    <th>Station</th>
                                    <th>Receipt No.</th>
                                    <th style={{ textAlign:'right' }}>Odometer</th>
                                    <th>Recorded By</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {topups.map((t, i) => (
                                    <tr key={t.id}>
                                        <td>{fmtD(t.topup_date)}</td>
                                        <td>
                                            <Tag color="blue" style={{ fontSize:11 }}>
                                                {t.registration_no}
                                            </Tag>
                                        </td>
                                        <td style={{ textAlign:'right', fontWeight:600, color:'#1890ff' }}>
                                            +{toN(t.litres_added).toFixed(1)} L
                                        </td>
                                        <td style={{ textAlign:'right' }}>
                                            {t.cost ? `E ${toN(t.cost).toLocaleString()}` : '—'}
                                        </td>
                                        <td>{t.fuel_station || '—'}</td>
                                        <td>{t.receipt_no || '—'}</td>
                                        <td style={{ textAlign:'right' }}>
                                            {t.odometer ? `${toN(t.odometer).toLocaleString()} km` : '—'}
                                        </td>
                                        <td>{t.recorded_by_name || '—'}</td>
                                        <td style={{ textAlign:'center' }}>
                                            <Button.Group size="small">
                                                <Button onClick={() => { setForm({ ...t }); setModalOpen(true); }}>
                                                    Edit
                                                </Button>
                                                <Button danger onClick={() => del(t.id)}>Del</Button>
                                            </Button.Group>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && topups.length === 0 && (
                                    <tr><td colSpan={9} style={{ textAlign:'center', padding:24, color:'#aaa' }}>
                                        No top-up records yet
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Spin>
            </div>

            {/* ── Add/Edit Modal ── */}
            <Modal title={form.id ? 'Edit Top-up' : 'Record Fuel Top-up'}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={save}
                okText="Save"
                confirmLoading={saving}
                destroyOnClose>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 14px' }}>
                    <div className="form-group" style={{ gridColumn:'1/-1' }}>
                        <label className="req">Vehicle</label>
                        <Select showSearch optionFilterProp="children" style={{ width:'100%' }}
                            value={form.vehicle_id || undefined}
                            onChange={v => setForm(f => ({ ...f, vehicle_id: v }))}>
                            {vehicles.map(v => (
                                <Option key={v.id} value={String(v.id)}>
                                    {v.registration_no} — {v.vehicle_type}
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <div className="form-group">
                        <label className="req">Date</label>
                        <DatePicker style={{ width:'100%' }}
                            value={form.topup_date ? moment(form.topup_date) : moment()}
                            onChange={d => setForm(f => ({ ...f, topup_date: d?.format('YYYY-MM-DD') }))}
                            format="DD MMM YYYY"/>
                    </div>
                    <div className="form-group">
                        <label className="req">Litres Added</label>
                        <InputNumber style={{ width:'100%' }} min={0.1} precision={1}
                            placeholder="e.g. 40"
                            value={form.litres_added || undefined}
                            onChange={v => setForm(f => ({ ...f, litres_added: v }))}/>
                    </div>
                    <div className="form-group">
                        <label>Cost (E)</label>
                        <InputNumber style={{ width:'100%' }} min={0} precision={2}
                            value={form.cost || undefined}
                            onChange={v => setForm(f => ({ ...f, cost: v }))}/>
                    </div>
                    <div className="form-group">
                        <label>Odometer at fill</label>
                        <InputNumber style={{ width:'100%' }} min={0}
                            value={form.odometer || undefined}
                            onChange={v => setForm(f => ({ ...f, odometer: v }))}/>
                    </div>
                    <div className="form-group">
                        <label>Fuel Station</label>
                        <Input value={form.fuel_station || ''}
                            onChange={e => setForm(f => ({ ...f, fuel_station: e.target.value }))}
                            placeholder="e.g. Puma Manzini"/>
                    </div>
                    <div className="form-group">
                        <label>Receipt No.</label>
                        <Input value={form.receipt_no || ''}
                            onChange={e => setForm(f => ({ ...f, receipt_no: e.target.value }))}/>
                    </div>
                    <div className="form-group" style={{ gridColumn:'1/-1' }}>
                        <label>Notes</label>
                        <Input.TextArea rows={2} value={form.notes || ''}
                            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}/>
                    </div>
                </div>
            </Modal>
        </div>
    );
}